import { computed, inject, Injectable, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { map, Observable, of, tap } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { Jwt } from '../../core/models/jwt.model';
import { jwtDecode } from 'jwt-decode';
import {
	PayloadRequest,
	PayloadResponse
} from '../../core/models/payload.model';
import { Reseller } from '../../core/models';
import { AuthDTO } from '../models/auth';
import { LOGIN_RESELLER_FORM } from '../models/form/login-reseller.form';
import { LOGIN_LEARNER_FORM } from '../models/form/login-discente.form';
import { UserRole } from '../../shared/enum';
import { ThemeService } from '../../shared/services/theme.service';
import { DomainService } from '../../core/services';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private localStorageService = inject(LocalStorageService);
	private apiService = inject(ApiService);
	private themeService = inject(ThemeService);
	private domainService = inject(DomainService);

	private currentUserData = signal<Jwt | null>(this.getTokenfromStorage());
	public currentRole = computed(() => this.currentUserData()?.role || null);
	public isAdmin = computed(
		() => this.currentUserData()?.role === UserRole.ADMIN
	);
	public isReseller = computed(
		() => this.currentUserData()?.role === UserRole.RESELLER
	);
	public isLearner = computed(
		() => this.currentUserData()?.role === UserRole.LEARNER
	);
	public currentUserName = computed(
		() => this.currentUserData()?.username || null
	);
	public currentDisplayName = computed(
		() => this.currentUserData()?.displayName || null
	);
	public isLoggedIn = computed(() => {
		const token = this.currentUserData();
		// Check expiration date
		return token && token.exp > Date.now() / 1000 ? true : false;
	});

	constructor(private router: Router) {}

	setCurrentUserAndTokenLocalStorage(token: string) {
		const decodedToken = this.decodeToken(atob(token));
		this.currentUserData.set(decodedToken);
		if (decodedToken.role === UserRole.RESELLER) {
			this.domainService.getAllAtecoCode();
			this.domainService.getAllNations();
			this.domainService.getAllMunicipalities();
		} else if (decodedToken.role === UserRole.ADMIN) {
			this.domainService.getAllNations();
			this.domainService.getAllMunicipalities();
		}
		this.localStorageService.setItem('token-auth', token);
	}

	login(form: FormGroup): Observable<void> {
		const data: AuthDTO = {
			username: form.get('email')?.value || form.get('fiscalCode')?.value,
			password: form.get('password')?.value
		};

		const payload = new PayloadRequest<AuthDTO>({ data: data });
		return this.apiService.post('auth/login', payload).pipe(
			map(res => {
				this.setCurrentUserAndTokenLocalStorage(res.data);
			})
		);
	}

	getTokenfromStorage(): Jwt | null {
		const tokenBase64 = this.localStorageService.getItem<string>('token-auth');
		if (tokenBase64) {
			try {
				const token = atob(tokenBase64);
				const decodedToken = jwtDecode<Jwt>(token);
				return decodedToken;
			} catch (error) {
				console.error('Error decoding token:', error);
				return null;
			}
		}
		return null;
	}

	getResellerByUserIdLogged(): Observable<PayloadResponse<Reseller>> {
		const token = this.currentUserData();
		if (!token) {
			this.logout();
			return of();
		}
		return this.apiService.get(`resellers/user/${token.sub}`);
	}

	logout(): void {
		this.localStorageService.removeItem('token-auth');
		this.currentUserData.set(null);
		// this.themeService.resetBaseColor();
		// this.themeService.resetDefaultFavicon();
		this.router.navigate(['/auth']);
	}

	decodeToken(token: string): Jwt {
		return jwtDecode<Jwt>(token);
	}

	// Login form config

	getResellerFormFields(): Observable<any> {
		return of(LOGIN_RESELLER_FORM);
	}

	getLearnerFormFields(): Observable<any> {
		return of(LOGIN_LEARNER_FORM);
	}

	configFormInputs(role: string): Observable<any> {
		if (role === 'rivenditore') {
			return this.getResellerFormFields();
		} else {
			return this.getLearnerFormFields();
		}
	}

	getDomainUrl(): Observable<void> {
		this.themeService.resetBaseColor?.();
		this.themeService.resetDefaultFavicon?.();
		document.body.classList.add('app-loading');
		document.body.classList.remove('app-ready');

		const url = new URL(window.location.href);
		const domainWithProtocol = `${url.protocol}//${url.host}`;
		const frontEndUrl = environment.frontEndUrl;

		if (this.checkFrontEndDomain()) {
			document.body.classList.remove('app-loading');
			document.body.classList.add('app-ready');
			return of(void 0);
		}

		const parts = url.hostname.split('.');

		const thirdLevelDomain = parts[0];

		return this.apiService
			.get<{ data: string[] }>('resellers/all-resellers-url')
			.pipe(
				tap(res => {
					if (res.data.includes(thirdLevelDomain)) {
						this.themeService
							.setBrandingByResellerUrl(thirdLevelDomain)
							.subscribe({
								next: () => {
									document.body.classList.remove('app-loading');
									document.body.classList.add('app-ready');
								},
								error: () => {
									document.body.classList.remove('app-loading');
									document.body.classList.add('app-ready');
								}
							});
					} else {
						window.location.href = `${frontEndUrl}/not-found`;
					}
				}),
				map(() => void 0)
			);
	}

	checkFrontEndDomain(): boolean {
		const url = new URL(window.location.href);
		const domainWithProtocol = `${url.protocol}//${url.host}`;
		const frontEndUrl = environment.frontEndUrl;

		// 1️⃣ Se è dominio FE → esci
		if (domainWithProtocol === frontEndUrl) {
			return true;
		}

		// 2️⃣ Se NON esiste subdomain → esci
		const hostname = url.hostname;

		if (hostname.toLowerCase().startsWith('www.')) {
			return true;
		}

		// Esclude IP
		if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return true;

		const parts = hostname.split('.');

		// es: playskills.it → solo 2 parti = NO subdomain
		if (parts.length < 3) {
			return true;
		}

		return false;
	}
}
